"use client";

import React from "react";
import { 
  Building, 
  Calendar, 
  Car, 
  User, 
  ChevronRight, 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  Phone,
  Sparkles
} from "lucide-react";
import { RealEstateLead } from "@/lib/demos/realEstateDb";

interface RealEstateKanbanProps {
  leads: RealEstateLead[];
  onSelectLead: (lead: RealEstateLead) => void;
  onUpdateStage: (leadId: number, newStage: RealEstateLead["stage"]) => void;
  currency?: "INR" | "USD";
}

interface StageColumnConfig {
  id: RealEstateLead["stage"];
  title: string;
  dotColor: string;
  badgeBg: string;
  description: string;
}

const STAGES: StageColumnConfig[] = [
  {
    id: "New Inquiries",
    title: "New Inquiries",
    dotColor: "bg-blue-400",
    badgeBg: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    description: "Portal inquiries & dossier downloads",
  },
  {
    id: "Site Visit Scheduled",
    title: "Site Visit Scheduled",
    dotColor: "bg-amber-400",
    badgeBg: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    description: "Calendar booked & chauffeur assigned",
  },
  {
    id: "Site Visit Completed",
    title: "Visit Completed",
    dotColor: "bg-purple-400",
    badgeBg: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    description: "Inspection done, shortlisting unit",
  },
  {
    id: "Booking Token",
    title: "Booking Token (EOI)",
    dotColor: "bg-orange-400",
    badgeBg: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    description: "EOI token deposited, unit blocked",
  },
  {
    id: "Sale Deed (Won)",
    title: "Sale Deed (Won)",
    dotColor: "bg-emerald-400",
    badgeBg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    description: "Agreement executed & revenue booked",
  },
  {
    id: "Lost",
    title: "Lost / Inactive",
    dotColor: "bg-slate-500",
    badgeBg: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    description: "Budget mismatch or cancelled",
  },
];

export default function RealEstateKanban({
  leads,
  onSelectLead,
  onUpdateStage,
  currency = "INR",
}: RealEstateKanbanProps) {
  // Drag & drop handlers
  const handleDragStart = (e: React.DragEvent, leadId: number) => {
    e.dataTransfer.setData("text/plain", leadId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetStage: RealEstateLead["stage"]) => {
    e.preventDefault();
    const leadIdStr = e.dataTransfer.getData("text/plain");
    const leadId = parseInt(leadIdStr, 10);
    if (!isNaN(leadId)) {
      onUpdateStage(leadId, targetStage);
    }
  };

  return (
    <div className="w-full overflow-x-auto pb-6 custom-scrollbar">
      <div className="flex gap-4 min-w-[1350px] items-start">
        {STAGES.map((col) => {
          const colLeads = leads.filter((l) => l.stage === col.id);

          // Calculate column total gross value
          const totalValINR = colLeads
            .filter((l) => l.currency === "INR")
            .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);
          const totalValUSD = colLeads
            .filter((l) => l.currency === "USD")
            .reduce((sum, l) => sum + (l.budget_numeric || 0), 0);

          const formattedTotal = () => {
            const inrCr = totalValINR > 0 ? `₹${(totalValINR / 10000000).toFixed(2)} Cr` : "";
            const usdM = totalValUSD > 0 ? `$${(totalValUSD / 1000000).toFixed(2)}M` : "";
            if (inrCr && usdM) return `${inrCr} • ${usdM}`;
            if (inrCr) return inrCr;
            if (usdM) return usdM;
            return "₹0";
          };

          return (
            <div
              key={col.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id)}
              className="flex-1 min-w-[250px] max-w-[280px] rounded-2xl bg-slate-900/90 border border-slate-800 shadow-xl flex flex-col max-h-[80vh] transition-all"
            >
              {/* Column Header */}
              <div className="p-3.5 border-b border-slate-800/90 bg-slate-950/60 rounded-t-2xl">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                    <h3 className="text-xs font-bold text-white uppercase tracking-wider font-heading">
                      {col.title}
                    </h3>
                  </div>
                  <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-md border ${col.badgeBg}`}>
                    {colLeads.length}
                  </span>
                </div>

                <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
                  <span>Gross Value:</span>
                  <span className="font-bold text-amber-400">{formattedTotal()}</span>
                </div>
              </div>

              {/* Cards Container */}
              <div className="p-3 space-y-3 overflow-y-auto flex-grow custom-scrollbar min-h-[140px]">
                {colLeads.length === 0 ? (
                  <div className="text-center py-8 px-2 border-2 border-dashed border-slate-800/80 rounded-xl">
                    <span className="text-[11px] text-slate-500 font-medium">No leads in stage</span>
                  </div>
                ) : (
                  colLeads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onClick={() => onSelectLead(lead)}
                      className="p-3.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/5 cursor-grab active:cursor-grabbing transition-all group relative"
                    >
                      {/* Top Property & Badge */}
                      <div className="flex items-start justify-between gap-1.5 mb-1.5">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-900 border border-slate-800 text-amber-400 uppercase tracking-wide">
                          {lead.property_type}
                        </span>

                        <span className="text-[10px] font-semibold text-slate-400">
                          #{lead.id}
                        </span>
                      </div>

                      {/* Lead Name */}
                      <h4 className="text-sm font-bold text-white group-hover:text-amber-400 transition-colors line-clamp-1">
                        {lead.name}
                      </h4>

                      {/* Property Interested */}
                      <div className="flex items-center gap-1 text-xs text-slate-300 font-medium mt-0.5 line-clamp-1">
                        <Building className="w-3 h-3 text-amber-400 shrink-0" />
                        <span>{lead.property_name}</span>
                      </div>

                      {/* Budget Pill */}
                      <div className="mt-2.5 pt-2 border-t border-slate-800/80 flex items-center justify-between">
                        <div className="text-xs font-extrabold text-amber-400">
                          {lead.budget_range}
                        </div>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-900 text-slate-400 border border-slate-800">
                          {lead.buyer_type}
                        </span>
                      </div>

                      {/* Site Visit Tag if present */}
                      {lead.site_visit_date && (
                        <div className="mt-2 text-[10px] p-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 flex items-center justify-between">
                          <div className="flex items-center gap-1 truncate">
                            <Clock className="w-3 h-3 shrink-0" />
                            <span className="truncate">{lead.site_visit_date}</span>
                          </div>
                          {lead.site_visit_pickup === 1 && (
                            <span className="flex items-center gap-0.5 font-bold text-emerald-400 ml-1 shrink-0">
                              <Car className="w-3 h-3" />
                              Pickup
                            </span>
                          )}
                        </div>
                      )}

                      {/* Next Stage Quick Button */}
                      <div className="mt-3 pt-2 border-t border-slate-800/60 flex items-center justify-between text-[10px]">
                        <span className="text-slate-500 truncate max-w-[120px]">
                          {lead.assigned_agent}
                        </span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentIdx = STAGES.findIndex((s) => s.id === lead.stage);
                            if (currentIdx < STAGES.length - 2) {
                              onUpdateStage(lead.id, STAGES[currentIdx + 1].id);
                            }
                          }}
                          className="flex items-center gap-1 text-slate-400 hover:text-amber-400 transition-colors font-bold"
                          title="Advance to next sales stage"
                        >
                          <span>Next</span>
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
